/**
 * jsPDF's built-in Helvetica font does NOT include glyphs for Unicode
 * subscripts (₀-₉), superscripts (⁰-⁹, ⁻, ⁺), arrows (→), middle dot (·),
 * minus sign (−), etc. When they're drawn they render as commas / boxes AND
 * their measured width is wrong, which makes splitTextToSize overflow the
 * right margin (questions appear "cut off").
 *
 * Normalize chemistry-flavored Unicode to ASCII equivalents so the built-in
 * font can both render and measure them correctly.
 */
const SUBS: Record<string, string> = {
  "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
  "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9",
};
const SUPS: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
  "⁻": "-", "⁺": "+",
};

// Greek letters commonly used in chem/physics formulas.
const GREEK: Record<string, string> = {
  "α": "alpha", "β": "beta", "γ": "gamma", "δ": "delta",
  "ε": "epsilon", "θ": "theta", "λ": "lambda", "μ": "mu",
  "π": "pi", "ρ": "rho", "σ": "sigma", "τ": "tau",
  "φ": "phi", "ω": "omega",
  "Α": "Alpha", "Β": "Beta", "Γ": "Gamma", "Δ": "Delta",
  "Θ": "Theta", "Λ": "Lambda", "Π": "Pi", "Σ": "Sigma",
  "Φ": "Phi", "Ω": "Omega",
};

export function pdfSafe(input: string): string {
  if (!input) return "";
  let out = "";
  let inSup = false;
  for (const ch of input) {
    if (ch in SUPS) {
      if (!inSup) {
        out += "^";
        inSup = true;
      }
      out += SUPS[ch];
      continue;
    }
    inSup = false;
    if (ch in SUBS) out += SUBS[ch];
    else if (ch in GREEK) out += GREEK[ch];
    else {
      switch (ch) {
        case "→": out += " -> "; break;
        case "←": out += " <- "; break;
        case "↔":
        case "⇄":
        case "⇆":
        case "⇌":
        case "⇋": out += " <=> "; break;
        case "⇒": out += " => "; break;
        case "⇐": out += " <= "; break;
        case "↑": out += "(g)"; break;
        case "↓": out += "(s)"; break;
        case "−":
        case "–":
        case "—": out += "-"; break;
        case "·":
        case "•": out += "*"; break;
        case "×": out += "x"; break;
        case "÷": out += "/"; break;
        case "±": out += "+/-"; break;
        case "∓": out += "-/+"; break;
        case "≈": out += "~"; break;
        case "≠": out += "!="; break;
        case "≡": out += "=="; break;
        case "≤": out += "<="; break;
        case "≥": out += ">="; break;
        case "∞": out += "inf"; break;
        case "∑": out += "sum"; break;
        case "∏": out += "prod"; break;
        case "∫": out += "integral"; break;
        case "√": out += "sqrt"; break;
        case "∂": out += "d"; break;
        case "∇": out += "grad"; break;
        case "∝": out += " proportional to "; break;
        case "∈": out += " in "; break;
        case "∉": out += " not in "; break;
        case "∅": out += "{}"; break;
        case "Å": out += "A"; break;
        case "℃": out += " degC"; break;
        case "℉": out += " degF"; break;
        case "°": out += " deg"; break;
        case "\u00A0": out += " "; break;
        case "\u200B":
        case "\u200C":
        case "\u200D":
        case "\uFEFF": break; // strip zero-width
        case "“":
        case "”":
        case "„": out += '"'; break;
        case "‘":
        case "’":
        case "‚": out += "'"; break;
        case "…": out += "..."; break;
        default: out += ch;
      }
    }
  }
  // Collapse the double-spaces our arrow replacements may introduce.
  return out.replace(/ {2,}/g, " ");
}